import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../Helper/api";
import html2pdf from "html2pdf.js/dist/html2pdf.min.js";
import { formatDate, formatDateTime } from "../i18n";
import {
  downloadTravelAllowanceExcel,
  escapeHtml,
  formatAmount,
  travelAllowanceFileName,
} from "../Helper/travelAllowance";

const stateBadgeClass = {
  PENDING: "bg-yellow-50 text-yellow-600 border-yellow-200",
  APPROVED: "bg-green-50 text-green-600 border-green-200",
  REJECTED: "bg-red-50 text-red-600 border-red-200",
};

function TravelAllowanceDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth.loginUser);
  const [allowance, setAllowance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/travel-allowances/${id}`);
      setAllowance(res.data);
    } catch (error) {
      toast.error(t("travelAllowance.detailsLoadError"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const showError = (error) => toast.error(error.response?.data?.message || t("travelAllowance.genericError"));

  const handleApprove = async () => {
    if (!window.confirm(t("travelAllowance.confirmApprove"))) return;
    try {
      const res = await api.patch(`/travel-allowances/approve/${id}`);
      if (res.data.state) {
        toast.success(t("travelAllowance.approveSuccess"));
        fetchDetails();
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt(t("list.rejectReasonPrompt"));
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      toast.error(t("travelAllowance.rejectReasonRequired"));
      return;
    }

    try {
      const res = await api.patch(`/travel-allowances/reject/${id}`, { rejectReason: reason });
      if (res.data.state) {
        toast.success(t("travelAllowance.rejectSuccess"));
        fetchDetails();
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleDownloadExcel = async () => {
    if (!allowance) return;
    toast.info(t("travelAllowance.excelPreparing"));
    try {
      await downloadTravelAllowanceExcel(allowance);
      toast.success(t("travelAllowance.excelSuccess"));
    } catch (error) {
      console.error("Excel download failed:", error);
      toast.error(t("travelAllowance.excelError"));
    }
  };

  const stateLabel = (state) => t(`leaveState.${state}`, { defaultValue: state || "-" });

  const handleDownloadPDF = async () => {
    if (!allowance) return;
    toast.info(t("travelAllowance.pdfPreparing"));

    // Kullanıcı kaynaklı tüm değerler HTML'e gömülmeden önce escape edilir
    const tx = (key, options) => escapeHtml(t(`travelAllowance.${key}`, options));
    const ownerName = escapeHtml(`${allowance.owner?.name || ""} ${allowance.owner?.surname || ""}`.trim());
    const headCell = (key, extraStyle = "") =>
      `<th style="padding: 8px 6px; font-weight: bold; color: #475569; ${extraStyle}">${tx(key)}</th>`;

    const element = document.createElement("div");
    element.innerHTML = `
      <div style="font-family: Arial, sans-serif; padding: 25px; color: #1e293b; line-height: 1.5;">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 25px;">
          <div>
            <h1 style="margin: 0; font-size: 24px; color: #1e293b; font-weight: bold; letter-spacing: 0.5px;">${tx("pdfTitle")}</h1>
            <p style="margin: 5px 0 0 0; font-size: 13px; color: #64748b;">${tx("pdfSubtitle")}</p>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #64748b;">${tx("requestId")}:</div>
            <div style="font-size: 14px; font-weight: bold; color: #3b82f6;">#${escapeHtml(String(allowance._id || "").slice(-8).toUpperCase())}</div>
          </div>
        </div>

        <!-- Info Boxes -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px;">
          <!-- Employee Card -->
          <div style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; background-color: #f8fafc;">
            <h3 style="margin: 0 0 10px 0; font-size: 13px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">${tx("employeeInfo")}</h3>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <tr>
                <td style="padding: 3px 0; color: #64748b; width: 40%;">${tx("fullName")}:</td>
                <td style="padding: 3px 0; font-weight: bold; color: #1e293b;">${ownerName}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; color: #64748b;">${tx("tc")}:</td>
                <td style="padding: 3px 0; font-weight: bold; color: #1e293b;">${escapeHtml(allowance.owner?.tc)}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; color: #64748b;">${tx("position")}:</td>
                <td style="padding: 3px 0; color: #334155;">${escapeHtml(allowance.owner?.position || "-")}</td>
              </tr>
            </table>
          </div>

          <!-- Travel Card -->
          <div style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; background-color: #f8fafc;">
            <h3 style="margin: 0 0 10px 0; font-size: 13px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">${tx("travelInfo")}</h3>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <tr>
                <td style="padding: 3px 0; color: #64748b; width: 40%;">${tx("project")}:</td>
                <td style="padding: 3px 0; font-weight: bold; color: #1e293b;">${escapeHtml(allowance.project)}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; color: #64748b;">${tx("travelDates")}:</td>
                <td style="padding: 3px 0; color: #334155;">${escapeHtml(formatDate(allowance.startDate) || "-")} - ${escapeHtml(formatDate(allowance.endDate) || "-")}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; color: #64748b;">${tx("totalDuration")}:</td>
                <td style="padding: 3px 0; font-weight: bold; color: #1e293b;">${tx("daysCount", { count: allowance.totalDays })}</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Table -->
        <h3 style="margin: 25px 0 10px 0; font-size: 14px; color: #1e293b; font-weight: bold;">${tx("pdfItems")}</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 25px;">
          <thead>
            <tr style="background-color: #f1f5f9; border-bottom: 2px solid #cbd5e1; text-align: left;">
              ${headCell("no", "width: 5%;")}
              ${headCell("date", "width: 12%;")}
              ${headCell("country", "width: 15%;")}
              ${headCell("city", "width: 12%;")}
              ${headCell("accommodation", "width: 15%;")}
              ${headCell("purpose", "width: 18%;")}
              ${headCell("amount", "text-align: right; width: 10%;")}
              ${headCell("currency", "text-align: center; width: 8%;")}
              ${headCell("rate", "text-align: right; width: 10%;")}
              ${headCell("tlEquivalent", "text-align: right; width: 12%;")}
            </tr>
          </thead>
          <tbody>
            ${(allowance.items || []).map((item, idx) => `
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 8px 6px; color: #64748b;">${idx + 1}</td>
                <td style="padding: 8px 6px; color: #334155;">${escapeHtml(formatDate(item.date) || "-")}</td>
                <td style="padding: 8px 6px; font-weight: 500; color: #1e293b;">${escapeHtml(item.country)}</td>
                <td style="padding: 8px 6px; color: #334155;">${escapeHtml(item.city)}</td>
                <td style="padding: 8px 6px; color: #475569;">${escapeHtml(item.accommodation || "-")}</td>
                <td style="padding: 8px 6px; color: #475569;">${escapeHtml(item.purpose)}</td>
                <td style="padding: 8px 6px; text-align: right; color: #1e293b;">${escapeHtml(formatAmount(item.dailyAllowance))}</td>
                <td style="padding: 8px 6px; text-align: center; font-weight: bold; color: #2563eb;">${escapeHtml(item.currency)}</td>
                <td style="padding: 8px 6px; text-align: right; color: #475569;">${escapeHtml(formatAmount(item.exchangeRate, 4))}</td>
                <td style="padding: 8px 6px; text-align: right; font-weight: bold; color: #1e293b;">${escapeHtml(formatAmount(item.tlEquivalent))} ₺</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <!-- Note & Total summary -->
        <div style="display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; margin-top: 15px; margin-bottom: 35px;">
          <div style="flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; background-color: #f8fafc;">
            <div style="font-size: 12px; font-weight: bold; color: #64748b; margin-bottom: 5px;">${tx("note")}:</div>
            <p style="margin: 0; font-size: 12px; color: #334155; white-space: pre-wrap;">${allowance.note ? escapeHtml(allowance.note) : tx("noNote")}</p>
          </div>
          <div style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; background-color: #eff6ff; text-align: right; min-width: 200px;">
            <div style="font-size: 11px; font-weight: bold; color: #3b82f6; text-transform: uppercase;">${tx("totalAmountShort")}:</div>
            <div style="font-size: 22px; font-weight: 900; color: #1d4ed8; margin-top: 5px;">${escapeHtml(formatAmount(allowance.totalAmount))} ₺</div>
          </div>
        </div>

        <!-- Approval / Signatures -->
        <div style="display: flex; justify-content: space-between; margin-top: 50px; font-size: 12px;">
          <div style="text-align: center; width: 45%; border-top: 1px solid #cbd5e1; padding-top: 10px;">
            <div style="font-weight: bold; color: #334155;">${tx("requester")}</div>
            <div style="color: #64748b; margin-top: 4px;">${ownerName}</div>
            <div style="color: #94a3b8; font-size: 10px; margin-top: 25px;">${tx("signature")}</div>
          </div>
          <div style="text-align: center; width: 45%; border-top: 1px solid #cbd5e1; padding-top: 10px;">
            <div style="font-weight: bold; color: #334155;">${tx("managerApproval", { status: stateLabel(allowance.state) })}</div>
            <div style="color: #64748b; margin-top: 4px;">${tx("management")}</div>
            <div style="color: #94a3b8; font-size: 10px; margin-top: 25px;">${tx("signatureStamp")}</div>
          </div>
        </div>
      </div>
    `;

    try {
      await html2pdf().from(element).set({
        margin: [0.4, 0.4, 0.4, 0.4],
        filename: travelAllowanceFileName(allowance, "pdf"),
        html2canvas: { scale: 2, letterRendering: true, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" }
      }).save();
      toast.success(t("travelAllowance.pdfSuccess"));
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error(t("travelAllowance.pdfError"));
    }
  };

  if (loading) {
    return <div className="text-center py-20 text-slate-500">{t("travelAllowance.loading")}</div>;
  }

  if (!allowance) {
    return (
      <div className="text-center py-20 text-slate-500">
        {t("travelAllowance.notFound")}
        <button
          onClick={() => navigate("/harcirahlar")}
          className="ml-4 bg-blue-600 text-white px-4 py-2 rounded"
        >
          {t("travelAllowance.backToList")}
        </button>
      </div>
    );
  }

  const isPending = allowance.state === "PENDING";
  const isApproved = allowance.state === "APPROVED";
  const isRejected = allowance.state === "REJECTED";

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">{t("travelAllowance.detailsTitle")}</h1>
          <p className="text-slate-500 mt-1">{t("travelAllowance.detailsDesc")}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/harcirahlar")}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50 transition"
          >
            {t("travelAllowance.backToList")}
          </button>
          <button
            onClick={handleDownloadPDF}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg shadow-lg shadow-blue-600/20 transition"
          >
            {t("travelAllowance.downloadPdf")} 📄
          </button>
          {isApproved && (
            <button
              onClick={handleDownloadExcel}
              className="bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2 rounded-lg shadow-lg shadow-green-600/20 transition"
            >
              {t("travelAllowance.downloadExcel")} 📥
            </button>
          )}
        </div>
      </div>

      {/* Grid of General Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Employee Info */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex flex-col justify-between">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">{t("travelAllowance.employeeInfo")}</h2>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.fullName")}</div>
              <div className="text-base font-semibold text-slate-800">
                {allowance.owner?.name} {allowance.owner?.surname}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.position")}</div>
              <div className="text-base font-medium text-slate-700">{allowance.owner?.position}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.tc")}</div>
              <div className="text-base font-medium text-slate-700">{allowance.owner?.tc}</div>
            </div>
          </div>
        </div>

        {/* Travel Info */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex flex-col justify-between">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">{t("travelAllowance.travelInfo")}</h2>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.project")}</div>
              <div className="text-base font-semibold text-slate-800">{allowance.project}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.travelDates")}</div>
              <div className="text-base font-medium text-slate-700">
                {formatDate(allowance.startDate) || "-"} - {formatDate(allowance.endDate) || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.totalDuration")}</div>
              <div className="text-base font-medium text-slate-700">
                {t("travelAllowance.daysCount", { count: allowance.totalDays })}
              </div>
            </div>
          </div>
        </div>

        {/* Request Status */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex flex-col justify-between">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">{t("travelAllowance.requestStatus")}</h2>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.status")}</div>
              <div className="mt-1">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold border ${stateBadgeClass[allowance.state] || ""}`}
                >
                  {stateLabel(allowance.state)}
                </span>
              </div>
            </div>

            {isRejected && (
              <div>
                <div className="text-xs text-red-400">{t("travelAllowance.rejectReason")}</div>
                <div className="text-sm font-medium text-red-600 bg-red-50 p-2 rounded border border-red-100 mt-1">
                  {allowance.rejectReason}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs text-slate-400">{t("travelAllowance.createdAt")}</div>
              <div className="text-sm text-slate-600">{formatDateTime(allowance.createdAt) || "-"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Logs Table */}
      <div className="bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-700">{t("travelAllowance.dailyBreakdown")}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100/50 text-slate-600 text-xs font-semibold uppercase border-b border-slate-100">
                <th className="px-6 py-3 w-10">{t("travelAllowance.no")}</th>
                <th className="px-6 py-3">{t("travelAllowance.date")}</th>
                <th className="px-6 py-3">{t("travelAllowance.country")}</th>
                <th className="px-6 py-3">{t("travelAllowance.city")}</th>
                <th className="px-6 py-3">{t("travelAllowance.accommodation")}</th>
                <th className="px-6 py-3">{t("travelAllowance.purpose")}</th>
                <th className="px-6 py-3 text-right">{t("travelAllowance.dailyAllowance")}</th>
                <th className="px-6 py-3 text-center w-24">{t("travelAllowance.currency")}</th>
                <th className="px-6 py-3 text-right">{t("travelAllowance.rate")}</th>
                <th className="px-6 py-3 text-right">{t("travelAllowance.tlEquivalent")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {allowance.items?.map((item, idx) => (
                <tr key={item._id || idx} className="hover:bg-slate-50/50 transition">
                  <td className="px-6 py-3 text-slate-400 font-medium">{idx + 1}</td>
                  <td className="px-6 py-3 text-slate-600">{formatDate(item.date) || "-"}</td>
                  <td className="px-6 py-3 text-slate-800 font-medium">{item.country}</td>
                  <td className="px-6 py-3 text-slate-700">{item.city}</td>
                  <td className="px-6 py-3 text-slate-600">{item.accommodation || "-"}</td>
                  <td className="px-6 py-3 text-slate-600">{item.purpose}</td>
                  <td className="px-6 py-3 text-right font-medium text-slate-700">
                    {formatAmount(item.dailyAllowance)}
                  </td>
                  <td className="px-6 py-3 text-center font-bold text-blue-600">{item.currency}</td>
                  <td className="px-6 py-3 text-right text-slate-600">{formatAmount(item.exchangeRate, 4)}</td>
                  <td className="px-6 py-3 text-right font-semibold text-slate-800">
                    {formatAmount(item.tlEquivalent)} ₺
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Note & Total sum card */}
      <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex flex-col md:flex-row md:items-start justify-between gap-6">
        <div className="flex flex-col gap-1.5 flex-1">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">{t("travelAllowance.note")}</h2>
          <p className="text-slate-700 bg-slate-50 p-4 rounded-lg border border-slate-100 whitespace-pre-wrap">
            {allowance.note || t("travelAllowance.noNote")}
          </p>
        </div>

        <div className="bg-slate-50 p-6 rounded-lg border border-slate-100 flex flex-col gap-1 items-end min-w-[240px]">
          <span className="text-xs font-semibold text-slate-500 uppercase">{t("travelAllowance.totalAmount")}</span>
          <span className="text-3xl font-black text-blue-600">{formatAmount(allowance.totalAmount)} ₺</span>
        </div>
      </div>

      {/* Admin actions (Approve / Reject) */}
      {auth?.userType === "admin" && isPending && (
        <div className="bg-white p-6 rounded-xl shadow-md border border-slate-100 flex justify-end gap-3">
          <button
            onClick={handleReject}
            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-bold"
          >
            {t("travelAllowance.reject")}
          </button>
          <button
            onClick={handleApprove}
            className="px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition font-bold"
          >
            {t("travelAllowance.approve")}
          </button>
        </div>
      )}
    </div>
  );
}

export default TravelAllowanceDetailsPage;
